// Shared axios instance with JWT injection + 401 handling.
import axios from "axios";

const BASE = process.env.REACT_APP_BACKEND_URL || "";

export const api = axios.create({
  baseURL: BASE,
  withCredentials: false, // we use Authorization header, not cookies
});

const TOKEN_KEY = "kapei_auth_token";
const EXP_KEY = "kapei_auth_expires_at";
const USER_KEY = "kapei_auth_username";

export const authStorage = {
  getToken: () => localStorage.getItem(TOKEN_KEY),
  getUsername: () => localStorage.getItem(USER_KEY),
  getExpiresAt: () => localStorage.getItem(EXP_KEY),
  set: (token, username, expiresAt) => {
    localStorage.setItem(TOKEN_KEY, token);
    localStorage.setItem(USER_KEY, username);
    localStorage.setItem(EXP_KEY, expiresAt);
  },
  clear: () => {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
    localStorage.removeItem(EXP_KEY);
  },
};

// Attach Authorization header to every request when token is present.
api.interceptors.request.use((config) => {
  const t = authStorage.getToken();
  if (t) {
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${t}`;
  }
  return config;
});

// Hook to react on global 401 (token expired / revoked). Subscribers are
// notified so the AuthContext can redirect to /login.
const subscribers = new Set();
export const onUnauthorized = (cb) => {
  subscribers.add(cb);
  return () => subscribers.delete(cb);
};

api.interceptors.response.use(
  (r) => r,
  (error) => {
    if (error?.response?.status === 401) {
      authStorage.clear();
      for (const cb of subscribers) {
        try { cb(); } catch (_) {}
      }
    }
    return Promise.reject(error);
  }
);

// Helpers for error message normalization (FastAPI may return list-of-errors
// for 422 validation responses — never put those directly in setState/JSX).
export function formatApiError(err, fallback = "Error desconocido") {
  const d = err?.response?.data?.detail;
  if (d == null) return err?.message || fallback;
  if (typeof d === "string") return d;
  if (Array.isArray(d)) {
    return d
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .join(" · ");
  }
  if (d && typeof d.msg === "string") return d.msg;
  return String(d);
}
